import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminBooksList() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get('/api/admin/books', { headers: authHeaders(), timeout: 10000 })
      .then(({ data }) => alive && setBooks(Array.isArray(data) ? data : []))
      .catch((e) => alive && setErr(e?.response?.data?.message || 'Failed to load books'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return books;
    return books.filter((b) =>
      [b.title, b.titleAr, b.author, b.category, b.genre, ...(b.tags || [])]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(needle))
    );
  }, [q, books]);

  const [ordering, setOrdering] = useState(false);
  const moveBook = async (id, direction) => {
    const index = books.findIndex(b => b._id === id);
    const next = index + direction;
    if (ordering || next < 0 || next >= books.length) return;
    const reordered = [...books];
    [reordered[index], reordered[next]] = [reordered[next], reordered[index]];
    setOrdering(true); setErr('');
    try { await axios.put('/api/admin/books/order', { ids: reordered.map(b => b._id) }, { headers: authHeaders() }); setBooks(reordered); }
    catch (e) { setErr(e.response?.data?.message || 'Could not save order.'); }
    finally { setOrdering(false); }
  };

  const onCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const { data } = await axios.post('/api/admin/books', {
        title: 'Untitled book',
        coverImage: '/uploads/placeholder.png',
      }, { headers: authHeaders() });
      navigate(`/admin/books/${data._id}`);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not create book');
      setCreating(false);
    }
  };

  const onDelete = async (b) => {
    if (!window.confirm(`Delete "${b.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/admin/books/${b._id}`, { headers: authHeaders() });
      setBooks((bs) => bs.filter((x) => x._id !== b._id));
    } catch (e) {
      setErr(e?.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <h1 className="admin-page-title">Books</h1>
        <p className="admin-page-sub">{books.length} total · New books stay in draft until you publish them.</p>
      </header>

      <div className="admin-toolbar">
        <input
          className="admin-input admin-search"
          placeholder="Search title, author, category…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="button" className="admin-btn" onClick={onCreate} disabled={creating}>
          {creating ? 'Creating…' : '+ New book'}
        </button>
      </div>

      {err && <div className="admin-toast admin-toast-error">{err}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Author</th>
              <th>Category</th>
              <th>Genre</th>
              <th>Status</th>
              <th>Quiz</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30 }}>Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, opacity: 0.7 }}>No books match.</td></tr>
            ) : filtered.map((b) => (
              <tr key={b._id}>
                <td>
                  <Link to={`/admin/books/${b._id}`} style={{ color: 'var(--admin-fg-strong)', fontWeight: 600 }}>
                    {b.title}
                  </Link>
                  {b.titleAr && <div style={{ fontSize: '.78rem', opacity: 0.6, marginTop: 2 }}>{b.titleAr}</div>}
                </td>
                <td>{b.author}</td>
                <td>{b.category || '—'}</td>
                <td>{b.genre || '—'}</td>
                <td><span className={`admin-tag ${b.publicationStatus === 'draft' ? 'admin-tag-user' : 'admin-tag-read'}`}>{b.publicationStatus === 'draft' ? 'Draft' : 'Published'}</span></td>
                <td>{b.hasQuiz ? <span className="admin-tag admin-tag-read">yes</span> : <span className="admin-tag admin-tag-user">no</span>}</td>
                <td>
                  <div className="admin-row-actions">
                    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" disabled={ordering || !!q || books[0]?._id === b._id} aria-label={`Move ${b.title} up`} onClick={() => moveBook(b._id, -1)}>Up</button>
                    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" disabled={ordering || !!q || books[books.length - 1]?._id === b._id} aria-label={`Move ${b.title} down`} onClick={() => moveBook(b._id, 1)}>Down</button>
                    <Link to={`/admin/books/${b._id}`} className="admin-btn admin-btn-ghost admin-btn-sm">Edit</Link>
                    <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => onDelete(b)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
